import { useState, useEffect, useCallback, useRef, useLayoutEffect } from 'react';

type Status = 'loading' | 'pending' | 'failed' | 'loaded';

type UseImageProps = {
    src?: string;
    onLoad?: (e: Event) => void;
    onError?: (e: Event) => void;
};

/**
 * useImage
 *
 * Prefetch an image before allowing the DOM to render the imagetag to avoid Cumulative Layout Shifts and flickering
 */
export const useImage = ({ src, onLoad, onError }: UseImageProps): Status => {
    const [status, setStatus] = useState<Status>('pending');

    const imageRef = useRef<HTMLImageElement | null>();

    useEffect(() => {
        setStatus(src ? 'loading' : 'pending');
    }, [src]);

    const load = useCallback(() => {
        if (!src) return;

        const img = new Image();
        img.src = src;

        img.onload = (event): void => {
            flush();
            setStatus('loaded');
            onLoad?.(event);
        };
        img.onerror = (error): void => {
            flush();
            setStatus('failed');
            onError?.(error as any);
        };

        imageRef.current = img;
    }, [src, onLoad, onError]);

    const flush = (): void => {
        if (imageRef.current) {
            imageRef.current.onload = null;
            imageRef.current.onerror = null;
            imageRef.current = null;
        }
    };

    useLayoutEffect(() => {
        if (status === 'loading') {
            load();
        }
        return (): void => flush();
    }, [status, load]);

    return status;
};
